import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Select } from '../components/Select';
import { Input } from '../components/Input';

type ManuscriptStatus = 'reading' | 'evaluating' | 'decision_pending' | 'offer_made' | 'declined';

interface RequestedManuscript {
  id: number;
  title: string;
  author: string;
  genre: string;
  wordCount: number;
  requestType: 'partial' | 'full';
  requestedDate: string;
  receivedDate: string;
  deadline: string;
  status: ManuscriptStatus;
  progress: number; // percentage
  daysRemaining: number;
}

export const RequestedManuscriptsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('deadline');

  // Mock data
  const manuscripts: RequestedManuscript[] = [
    {
      id: 1,
      title: 'The Silent Shore',
      author: 'Emma Rodriguez',
      genre: 'Literary Fiction',
      wordCount: 85000,
      requestType: 'full',
      requestedDate: '2024-02-15',
      receivedDate: '2024-02-18',
      deadline: '2024-04-18',
      status: 'reading',
      progress: 45,
      daysRemaining: 23,
    },
    {
      id: 2,
      title: 'Code Breakers',
      author: 'Michael Chen',
      genre: 'Thriller',
      wordCount: 95000,
      requestType: 'full',
      requestedDate: '2024-02-20',
      receivedDate: '2024-02-22',
      deadline: '2024-04-22',
      status: 'evaluating',
      progress: 100,
      daysRemaining: 27,
    },
    {
      id: 3,
      title: 'Whispers in the Dark',
      author: 'Sarah Mitchell',
      genre: 'Horror',
      wordCount: 78000,
      requestType: 'partial',
      requestedDate: '2024-02-10',
      receivedDate: '2024-02-12',
      deadline: '2024-03-12',
      status: 'decision_pending',
      progress: 100,
      daysRemaining: -15,
    },
    {
      id: 4,
      title: 'Echoes of Tomorrow',
      author: 'David Park',
      genre: 'Science Fiction',
      wordCount: 102000,
      requestType: 'full',
      requestedDate: '2024-03-01',
      receivedDate: '2024-03-01',
      deadline: '2024-05-01',
      status: 'reading',
      progress: 15,
      daysRemaining: 45,
    },
    {
      id: 5,
      title: 'The Last Garden',
      author: 'Jennifer Williams',
      genre: 'Fantasy',
      wordCount: 120000,
      requestType: 'full',
      requestedDate: '2024-01-15',
      receivedDate: '2024-01-18',
      deadline: '2024-03-18',
      status: 'offer_made',
      progress: 100,
      daysRemaining: -10,
    },
    {
      id: 6,
      title: 'Beyond the Horizon',
      author: 'Robert Taylor',
      genre: 'Adventure',
      wordCount: 88000,
      requestType: 'partial',
      requestedDate: '2024-03-05',
      receivedDate: '',
      deadline: '2024-04-05',
      status: 'reading',
      progress: 0,
      daysRemaining: 40,
    },
  ];

  const getStatusColor = (status: ManuscriptStatus): 'default' | 'success' | 'warning' | 'error' | 'primary' => {
    switch (status) {
      case 'reading':
        return 'primary';
      case 'evaluating':
        return 'warning';
      case 'decision_pending':
        return 'error';
      case 'offer_made':
        return 'success';
      case 'declined':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: ManuscriptStatus): string => {
    switch (status) {
      case 'reading':
        return 'Reading';
      case 'evaluating':
        return 'Evaluating';
      case 'decision_pending':
        return 'Decision Pending';
      case 'offer_made':
        return 'Offer Made';
      case 'declined':
        return 'Declined';
      default:
        return status;
    }
  };

  const getDeadlineColor = (daysRemaining: number): string => {
    if (daysRemaining < 0) return theme.colors.error;
    if (daysRemaining <= 7) return theme.colors.warning;
    return theme.colors.textSecondary;
  };

  // Filter and sort manuscripts
  let filteredManuscripts = manuscripts.filter(m => {
    const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         m.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || m.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Sort manuscripts
  filteredManuscripts.sort((a, b) => {
    switch (sortBy) {
      case 'deadline':
        return a.daysRemaining - b.daysRemaining;
      case 'title':
        return a.title.localeCompare(b.title);
      case 'author':
        return a.author.localeCompare(b.author);
      case 'received':
        return new Date(b.receivedDate || '').getTime() - new Date(a.receivedDate || '').getTime();
      default:
        return 0;
    }
  });

  // Calculate stats
  const totalManuscripts = manuscripts.length;
  const readingCount = manuscripts.filter(m => m.status === 'reading').length;
  const evaluatingCount = manuscripts.filter(m => m.status === 'evaluating').length;
  const pendingCount = manuscripts.filter(m => m.status === 'decision_pending').length;
  const overdueCount = manuscripts.filter(m => m.daysRemaining < 0).length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Requested Manuscripts
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Track and manage all manuscripts you've requested
          </p>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '32px',
        }}>
          <Card style={{ backgroundColor: theme.colors.white, textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.primary,
              marginBottom: '4px',
            }}>
              {totalManuscripts}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Total Requests
            </div>
          </Card>

          <Card style={{ backgroundColor: theme.colors.white, textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.info,
              marginBottom: '4px',
            }}>
              {readingCount}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Currently Reading
            </div>
          </Card>

          <Card style={{ backgroundColor: theme.colors.white, textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.warning,
              marginBottom: '4px',
            }}>
              {evaluatingCount}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Evaluating
            </div>
          </Card>

          <Card style={{ backgroundColor: theme.colors.white, textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.error,
              marginBottom: '4px',
            }}>
              {pendingCount}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Decision Pending
            </div>
          </Card>

          <Card style={{ backgroundColor: theme.colors.white, textAlign: 'center' }}>
            <div style={{
              fontSize: '32px',
              fontWeight: '500',
              color: theme.colors.error,
              marginBottom: '4px',
            }}>
              {overdueCount}
            </div>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Overdue
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
          }}>
            <Input
              label="Search"
              placeholder="Search by title or author..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <Select
              label="Filter by Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'reading', label: 'Reading' },
                { value: 'evaluating', label: 'Evaluating' },
                { value: 'decision_pending', label: 'Decision Pending' },
                { value: 'offer_made', label: 'Offer Made' },
                { value: 'declined', label: 'Declined' },
              ]}
            />

            <Select
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              options={[
                { value: 'deadline', label: 'Deadline (Urgent First)' },
                { value: 'received', label: 'Recently Received' },
                { value: 'title', label: 'Title (A-Z)' },
                { value: 'author', label: 'Author (A-Z)' },
              ]}
            />
          </div>
        </Card>

        {/* Manuscripts List */}
        {filteredManuscripts.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '60px 40px' }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
            }}>
              📚
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '8px',
            }}>
              No manuscripts found
            </h3>
            <p style={{
              fontSize: '16px',
              color: theme.colors.textSecondary,
            }}>
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Request manuscripts from your query inbox to see them here'}
            </p>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredManuscripts.map((manuscript) => (
              <Card key={manuscript.id}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px',
                    }}>
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        margin: 0,
                      }}>
                        {manuscript.title}
                      </h3>
                      <Badge variant={getStatusColor(manuscript.status)}>
                        {getStatusLabel(manuscript.status)}
                      </Badge>
                      <Badge variant="default">
                        {manuscript.requestType === 'full' ? 'Full MS' : 'Partial'}
                      </Badge>
                    </div>

                    <p style={{
                      fontSize: '15px',
                      color: theme.colors.textSecondary,
                      margin: '0 0 12px 0',
                    }}>
                      by {manuscript.author} • {manuscript.genre} • {manuscript.wordCount.toLocaleString()} words
                    </p>

                    <div style={{
                      display: 'flex',
                      gap: '20px',
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      marginBottom: '12px',
                    }}>
                      <div>
                        <strong>Requested:</strong> {new Date(manuscript.requestedDate).toLocaleDateString()}
                      </div>
                      {manuscript.receivedDate && (
                        <div>
                          <strong>Received:</strong> {new Date(manuscript.receivedDate).toLocaleDateString()}
                        </div>
                      )}
                      <div style={{ color: getDeadlineColor(manuscript.daysRemaining) }}>
                        <strong>Deadline:</strong> {new Date(manuscript.deadline).toLocaleDateString()}
                        {manuscript.daysRemaining < 0
                          ? ` (${Math.abs(manuscript.daysRemaining)} days overdue)`
                          : ` (${manuscript.daysRemaining} days left)`}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '13px',
                        color: theme.colors.textSecondary,
                        marginBottom: '6px',
                      }}>
                        <span>Reading Progress</span>
                        <span>{manuscript.progress}%</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: theme.colors.backgroundAlt,
                        borderRadius: theme.borderRadius.small,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${manuscript.progress}%`,
                          height: '100%',
                          backgroundColor: manuscript.progress === 100 ? theme.colors.success : theme.colors.primary,
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginLeft: '24px',
                  }}>
                    {manuscript.receivedDate ? (
                      <>
                        <Button
                          variant="primary"
                          onClick={() => window.location.href = `/agent/manuscripts/${manuscript.id}/read`}
                        >
                          {manuscript.progress === 100 ? 'Review' : 'Continue Reading'}
                        </Button>
                        {manuscript.status === 'decision_pending' && (
                          <Button
                            variant="outline"
                            onClick={() => window.alert('Make decision functionality')}
                          >
                            Make Decision
                          </Button>
                        )}
                      </>
                    ) : (
                      <Badge variant="warning">Awaiting Submission</Badge>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
